import { useState, useEffect } from "react";

type Product = {
  id: number;
  name: string;
  shortDesc: string;
  image: string;
  features: string[];
  overview: {
    description: string;
    useCases: string[];
    certifications: string[];
  };
  specs: { label: string; value: string }[];
  drawing: string;
};

type Tab = "overview" | "specs" | "drawing";

interface Props {
  product: Product | null;
  productIndex: number;
  onClose: () => void;
}

const TAB_LIST: { key: Tab; label: string; icon: string }[] = [
  { key: "overview", label: "제품개요", icon: "ri-file-text-line" },
  { key: "specs", label: "제품사양", icon: "ri-list-check-3" },
  { key: "drawing", label: "제품도면", icon: "ri-blueprint-line" },
];

const ProductDetailModal = ({ product, productIndex, onClose }: Props) => {
  const [activeTab, setActiveTab] = useState<Tab>("overview");

  useEffect(() => {
    if (product) {
      setActiveTab("overview");
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [product]);

  if (!product) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60" />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl overflow-hidden w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "modalFadeIn 0.22s ease" }}
      >
        {/* Header */}
        <div className="flex items-center gap-4 px-6 pt-6 pb-4 border-b border-gray-100">
          <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-xl shrink-0">
            <span className="text-white font-black text-sm">
              {String(productIndex + 1).padStart(2, "0")}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-black text-gray-900 leading-tight truncate">
              {product.name}
            </h2>
            <p className="text-gray-500 text-xs mt-0.5 truncate">{product.shortDesc}</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer shrink-0"
          >
            <i className="ri-close-line text-gray-600 text-lg"></i>
          </button>
        </div>

        {/* Tab Bar */}
        <div className="flex gap-1 px-6 pt-3 pb-0 border-b border-gray-100">
          {TAB_LIST.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-t-lg transition-all cursor-pointer whitespace-nowrap border-b-2 -mb-px ${
                activeTab === tab.key
                  ? "text-orange-500 border-orange-500 bg-orange-50/60"
                  : "text-gray-400 border-transparent hover:text-gray-600 hover:bg-gray-50"
              }`}
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-sm`}></i>
              </div>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1">
          {/* 제품개요 */}
          {activeTab === "overview" && (
            <div className="p-6 flex flex-col gap-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="w-full h-52 rounded-xl overflow-hidden bg-gray-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="flex flex-col justify-between gap-4">
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-2">제품 설명</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {product.overview.description}
                    </p>
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-gray-900 mb-2">주요 특징</h3>
                    <ul className="space-y-1.5">
                      {product.features.map((f) => (
                        <li key={f} className="flex items-center gap-2 text-sm text-gray-600">
                          <div className="w-4 h-4 flex items-center justify-center shrink-0">
                            <i className="ri-check-line text-orange-500 text-sm"></i>
                          </div>
                          {f}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-orange-50 rounded-xl p-4">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-building-2-line text-orange-500 text-sm"></i>
                    </div>
                    주요 적용 현장
                  </h3>
                  <ul className="space-y-1.5">
                    {product.overview.useCases.map((uc) => (
                      <li key={uc} className="flex items-start gap-2 text-sm text-gray-600">
                        <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                          <i className="ri-map-pin-2-line text-orange-400 text-xs"></i>
                        </div>
                        {uc}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <i className="ri-medal-line text-gray-500 text-sm"></i>
                    </div>
                    인증 및 규격
                  </h3>
                  <ul className="space-y-1.5">
                    {product.overview.certifications.map((cert) => (
                      <li key={cert} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className="ri-shield-check-line text-gray-400 text-xs"></i>
                        </div>
                        {cert}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* 제품사양 */}
          {activeTab === "specs" && (
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-sm font-bold text-gray-900 mb-1">세부 사양</h3>
                <p className="text-xs text-gray-400">현장 조건에 따라 사양이 변경될 수 있습니다. 정확한 사양은 문의 바랍니다.</p>
              </div>
              <div className="rounded-xl overflow-hidden border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="text-left px-5 py-3 text-gray-500 font-semibold text-xs uppercase tracking-wide w-2/5">
                        항목
                      </th>
                      <th className="text-left px-5 py-3 text-gray-500 font-semibold text-xs uppercase tracking-wide">
                        사양
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {product.specs.map((spec, i) => (
                      <tr
                        key={spec.label}
                        className={i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}
                      >
                        <td className="px-5 py-3.5 text-gray-500 font-medium text-xs border-t border-gray-100">
                          {spec.label}
                        </td>
                        <td className="px-5 py-3.5 text-gray-900 font-semibold text-sm border-t border-gray-100">
                          {spec.value}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 flex items-start gap-2 text-xs text-gray-400">
                <div className="w-4 h-4 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-information-line text-xs"></i>
                </div>
                상기 규격은 표준 제품 기준이며, 주문 제작 시 별도 협의가 가능합니다.
              </div>
            </div>
          )}

          {/* 제품도면 */}
          {activeTab === "drawing" && (
            <div className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-bold text-gray-900 mb-1">제품 도면</h3>
                  <p className="text-xs text-gray-400">치수 단위: mm / 상세 도면은 견적 문의 시 제공됩니다.</p>
                </div>
                <button
                  onClick={() => {
                    const link = document.createElement("a");
                    link.href = product.drawing;
                    link.download = `${product.slug}-drawing.jpg`;
                    link.click();
                  }}
                  className="flex items-center gap-1.5 text-xs font-semibold text-orange-500 border border-orange-200 rounded-lg px-3 py-1.5 hover:bg-orange-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-download-line text-xs"></i>
                  </div>
                  도면 저장
                </button>
              </div>
              <div className="w-full rounded-xl overflow-hidden bg-gray-50 border border-gray-200">
                <img
                  src={product.drawing}
                  alt={`${product.name} 도면`}
                  className="w-full object-contain"
                  style={{ maxHeight: "420px" }}
                />
              </div>
              <div className="mt-4 bg-orange-50 rounded-xl p-4 flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-orange-500/10 rounded-lg shrink-0">
                  <i className="ri-customer-service-2-line text-orange-500 text-base"></i>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">상세 도면이 필요하신가요?</p>
                  <p className="text-xs text-gray-500 mt-0.5">CAD 파일 및 상세 시공 도면은 견적 문의 시 제공해 드립니다.</p>
                </div>
                <button
                  onClick={() => {
                    onClose();
                    setTimeout(() => {
                      const el = document.querySelector("#contact");
                      if (el) el.scrollIntoView({ behavior: "smooth" });
                    }, 200);
                  }}
                  className="flex items-center gap-1 text-orange-500 text-xs font-bold whitespace-nowrap cursor-pointer hover:underline"
                >
                  문의하기 <i className="ri-arrow-right-line"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: scale(0.97) translateY(10px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default ProductDetailModal;
